<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  /** v-model binding: scalar in single mode, array of values in multiSelect mode */
  modelValue: { type: [String, Number, Array, null], default: '' },
  /** Array of { value, label, description? } option objects */
  options: { type: Array, required: true },
  /** Placeholder text when no option is selected */
  placeholder: { type: String, default: 'Select an option' },
  /** Whether the dropdown is disabled */
  disabled: { type: Boolean, default: false },
  /** Whether to show validation error styling */
  isInvalid: { type: Boolean, default: false },
  /** Border/accent color variant */
  variant: {
    type: String,
    default: 'primary',
    validator: (v) => ['primary', 'secondary'].includes(v)
  },
  /** Enable checkbox multi-select mode (modelValue becomes an array) */
  multiSelect: { type: Boolean, default: false },
  /** In multiSelect mode, cap the number of selections; remaining options get disabled */
  maxSelections: { type: Number, default: null }
})

const emit = defineEmits(['update:modelValue'])

const isOpen = ref(false)
const dropdownRef = ref(null)

const selectedValues = computed(() => (Array.isArray(props.modelValue) ? props.modelValue : []))

// Case-insensitive comparison for string values: stored emails may be
// lowercase-normalized while option values keep the users-collection casing.
function sameValue(a, b) {
  if (typeof a === 'string' && typeof b === 'string') {
    return a.toLowerCase() === b.toLowerCase()
  }
  return a === b
}

const isValueSelected = (value) => selectedValues.value.some(v => sameValue(v, value))

// In multi mode, selected values missing from the options list (e.g. an owner
// who was deactivated or excluded from the agency list) are appended as
// "phantom" options so they render checked and can be unchecked by the user.
const displayOptions = computed(() => {
  if (!props.multiSelect) return props.options
  const phantoms = selectedValues.value
    .filter(v => !props.options.some(opt => sameValue(opt.value, v)))
    .map(v => ({ value: v, label: String(v) }))
  return phantoms.length ? [...props.options, ...phantoms] : props.options
})

const selectedOption = computed(() => {
  if (props.multiSelect) return null
  if (props.modelValue === null || props.modelValue === undefined) return null
  return props.options.find(opt => sameValue(opt.value, props.modelValue)) || null
})

const hasSelection = computed(() =>
  props.multiSelect ? selectedValues.value.length > 0 : !!selectedOption.value
)

const displayText = computed(() => {
  if (!props.multiSelect) {
    return selectedOption.value ? selectedOption.value.label : props.placeholder
  }
  if (selectedValues.value.length === 0) return props.placeholder
  if (selectedValues.value.length === 1) {
    // With exactly one selection ALWAYS show its value — never "1 Selected"
    const option = displayOptions.value.find(opt => sameValue(opt.value, selectedValues.value[0]))
    return option ? option.label : String(selectedValues.value[0])
  }
  return `${selectedValues.value.length} Selected`
})

function isOptionSelected(option) {
  return props.multiSelect
    ? isValueSelected(option.value)
    : sameValue(props.modelValue, option.value)
}

function isOptionDisabled(option) {
  if (option.disabled) return true
  if (!props.multiSelect || props.maxSelections == null) return false
  return selectedValues.value.length >= props.maxSelections && !isValueSelected(option.value)
}

function toggle() {
  if (props.disabled) return
  isOpen.value = !isOpen.value
  if (isOpen.value) nextTick(updateMenuPlacement)
}

function selectOption(option) {
  if (isOptionDisabled(option)) return
  if (props.multiSelect) {
    const current = selectedValues.value
    const newValue = isValueSelected(option.value)
      ? current.filter(v => !sameValue(v, option.value))
      : [...current, option.value]
    emit('update:modelValue', newValue)
    // Menu stays open in multi mode so users can pick several options
    return
  }
  emit('update:modelValue', option.value)
  isOpen.value = false
}

/*
 * Menu placement. The menu opens below the trigger and flips above it when the space below
 * cannot hold it; either way its height is clamped to the room that side actually has, so
 * the whole menu stays inside the viewport instead of the last option being cut off by the
 * bottom edge of the screen — the list scrolls internally instead. Recomputed while open,
 * because scrolling or resizing changes which side fits.
 */
const MENU_MAX_HEIGHT = 324 // keep in sync with max-height in the styles below
const MENU_MIN_HEIGHT = 96 // never squeeze past ~2 options; scroll inside the menu instead
const VIEWPORT_MARGIN = 8

const menuRef = ref(null)
const dropUp = ref(false)
const menuMaxHeight = ref(MENU_MAX_HEIGHT)

function updateMenuPlacement() {
  // The menu is out of flow, so the wrapper's box is the trigger's box.
  const trigger = dropdownRef.value
  const menu = menuRef.value
  if (!trigger || !menu) return

  const rect = trigger.getBoundingClientRect()
  const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN
  const spaceAbove = rect.top - VIEWPORT_MARGIN
  // scrollHeight is the content height, so it is not itself capped by max-height.
  const needed = Math.min(menu.scrollHeight, MENU_MAX_HEIGHT)

  // Below is the default: it only flips when below cannot hold the menu AND above has more
  // room, so a cramped viewport does not make the menu jump sides on every open.
  const flip = needed > spaceBelow && spaceAbove > spaceBelow
  const available = Math.max(flip ? spaceAbove : spaceBelow, MENU_MIN_HEIGHT)

  dropUp.value = flip
  menuMaxHeight.value = Math.min(MENU_MAX_HEIGHT, available)
}

function onViewportChange() {
  if (isOpen.value) updateMenuPlacement()
}

function handleClickOutside(event) {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  // Capture: the trigger can also be moved by an ancestor scrolling, not just the window.
  window.addEventListener('scroll', onViewportChange, true)
  window.addEventListener('resize', onViewportChange)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('scroll', onViewportChange, true)
  window.removeEventListener('resize', onViewportChange)
})
</script>

<template>
  <div
    ref="dropdownRef"
    class="bcs-dropdown-select"
    :class="{ disabled }"
    :style="{
      '--bcs-accent': `var(--bs-${variant})`,
      '--bcs-accent-rgb': `var(--bs-${variant}-rgb)`
    }"
  >
    <button
      type="button"
      class="bcs-dropdown-select__trigger"
      :class="{ 'is-open': isOpen, 'is-invalid': isInvalid, 'has-value': hasSelection }"
      :disabled="disabled"
      @click="toggle"
    >
      <span class="bcs-dropdown-select__label" :class="{ 'text-muted': !hasSelection }">
        {{ displayText }}
      </span>
      <i class="bi bi-caret-down-fill bcs-dropdown-select__icon" :class="{ 'rotated': isOpen }"></i>
    </button>
    <Transition name="dropdown-fade">
      <ul
        v-if="isOpen"
        ref="menuRef"
        class="bcs-dropdown-select__menu"
        :class="{ 'is-drop-up': dropUp }"
        :style="{ maxHeight: `${menuMaxHeight}px` }">
        <li
          v-for="option in displayOptions"
          :key="option.value"
          class="bcs-dropdown-select__item"
          :class="{
            'is-selected': isOptionSelected(option),
            'is-disabled': isOptionDisabled(option)
          }"
          :data-value="option.value"
          @click="selectOption(option)"
        >
          <div v-if="multiSelect" class="bcs-dropdown-select__item-check">
            <input
              type="checkbox"
              class="form-check-input"
              :checked="isOptionSelected(option)"
              :disabled="isOptionDisabled(option)"
              tabindex="-1"
            />
            <span class="bcs-dropdown-select__item-label">{{ option.label }}</span>
          </div>
          <template v-else>
            <span class="bcs-dropdown-select__item-label">{{ option.label }}</span>
            <span v-if="option.description" class="bcs-dropdown-select__item-desc">{{ option.description }}</span>
          </template>
        </li>
      </ul>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.bcs-dropdown-select {
  position: relative;

  &.disabled {
    opacity: 0.65;
    pointer-events: none;
  }

  &__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5;
    color: var(--bs-body-color);
    background-color: var(--bs-white);
    border: 1px solid var(--bcs-accent);
    border-radius: 0.375rem;
    cursor: pointer;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:hover:not(:disabled) {
      border-color: var(--bcs-accent);
    }

    &.is-open {
      border-color: var(--bcs-accent);
      box-shadow: 0 0 0 0.2rem rgba(var(--bcs-accent-rgb), 0.25);
    }

    &.is-invalid {
      border-color: var(--bs-danger);
    }

    &.has-value {
      color: var(--bcs-accent);
      font-weight: 500;
    }
  }

  &__label {
    flex: 1;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__icon {
    font-size: 0.65rem;
    color: var(--bcs-accent);
    transition: transform 0.2s ease;
    margin-left: 0.5rem;

    &.rotated {
      transform: rotate(180deg);
    }
  }

  // Menu panel + items mirror the FilterDropdown (Coverages filter) styling
  // so every dropdown in the app shares one visual language.
  &__menu {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 1050;
    margin-top: 2px;
    padding: 0.5rem 0;
    list-style: none;
    background-color: var(--bs-white);
    border: 1px solid var(--bs-border-color);
    border-radius: 0.375rem;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
    /* Ceiling for a roomy viewport; updateMenuPlacement() lowers it inline when the
       chosen side has less room than this. */
    max-height: 324px;
    overflow-y: auto;
    width: auto;
    min-width: 100%;

    /* Flipped above the trigger when there is not enough room below. */
    &.is-drop-up {
      top: auto;
      bottom: 100%;
      margin-top: 0;
      margin-bottom: 2px;
    }
  }

  &__item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    box-sizing: border-box;
    min-height: 24px;
    padding: 4px 16px;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      background-color: var(--gray-200);
    }

    // Selected items (single and multi mode) get the Eggplant primary background
    &.is-selected {
      background-color: var(--bs-primary);

      .bcs-dropdown-select__item-label {
        color: #fff;
      }

      .bcs-dropdown-select__item-desc {
        color: rgba(255, 255, 255, 0.8);
      }


      &:hover {
        background-color: var(--bs-primary);
      }
    }

    &.is-disabled {
      opacity: 0.5;
      // No pointer-events:none — it would suppress the not-allowed cursor.
      // Clicks are guarded in selectOption() via isOptionDisabled().
      cursor: not-allowed;

      &:hover {
        background-color: transparent;
      }

      .bcs-dropdown-select__item-label {
        color: var(--bs-gray-600);
      }

      .form-check-input {
        cursor: not-allowed;
      }
    }

    &-check {
      display: flex;
      align-items: center;

      .form-check-input {
        // Vertical margin matches the 6px the checked halo below paints outside the box
        // (4px outline + 2px offset), so the halo stops at the content-box edge and the
        // item's 4px vertical padding stays visible instead of being overlapped.
        margin: 6px 0.5rem 6px 0;
        flex-shrink: 0;
        cursor: pointer;
        width: 16px;
        height: 16px;
        // Unchecked: Gray 700 border + 4px radius.
        border: 1px solid var(--bs-gray-700);
        border-radius: 4px;
        // Enlarge the check glyph a bit within the box.
        background-size: 90% 90%;

        // 4px outer halo, offset 2px outside the box, kept while checked.
        // outline + outline-offset paint outside the element so the checkbox
        // size/layout stay unchanged.
        &:checked {
          background-color: var(--bs-primary);
          border: none;
          outline: 4px solid #6b36dc;
          outline-offset: 2px;
          box-shadow: none;
        }
      }
    }

    &-label {
      font-family: 'DM Sans', sans-serif;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%; // 24px
      color: var(--bs-gray-900, #212529);
    }

    &-desc {
      font-size: 0.8rem;
      color: var(--bs-gray-600);
      margin-top: 2px;
    }
  }
}

// Transition
.dropdown-fade-enter-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.dropdown-fade-leave-active {
  transition: opacity 0.1s ease, transform 0.1s ease;
}
.dropdown-fade-enter-from {
  opacity: 0;
  transform: translateY(-4px);
}
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
